import { ActionReducerMapBuilder, AsyncThunk, PayloadAction, createAsyncThunk, createListenerMiddleware, createSlice } from '@reduxjs/toolkit';
import { FirebaseError } from 'firebase/app';
import type { UserCredential, UserInfo, UserMetadata } from 'firebase/auth';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { auth } from '../firebase';
import type { RootState } from '../store';
import { ErrorNotificationParams, InfoNotificationParams, NeutralNotificationParams, SuccessNotificationParams, notify } from './notifications';
import { saveUser, startListeningUser, stopListeningUser } from '../firestore/users';
import { getListCount, saveList, startListeningUserLists, stopListeningUserLists } from '../firestore/products';
import { ProductList, getSelectedList } from './products';
const LOCAL_STORAGE_VERSION: string = "1"; // change if local storage model changes
const LOCAL_STORAGE_KEY: string = `USERS-${LOCAL_STORAGE_VERSION}`;


export interface SignInPayload {
  email: string;
  password: string;
}

export interface AppUser extends UserInfo {
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: UserMetadata;
  providerData: UserInfo[];
  tenantId: string | null;
  isNewUser?: boolean;
}

const fromUserCredential = (userCredential: UserCredential): AppUser => {
  const user = userCredential.user.toJSON() as AppUser;
  const additionalUserInfo = getAdditionalUserInfo(userCredential);
  user.isNewUser = additionalUserInfo?.isNewUser || false;
  return user;
}

interface SignInError {
  errorMessage: string,
  errorCode: string,
}

export const callCreateUserWithEmailAndPassword = createAsyncThunk<
  AppUser,
  SignInPayload,
  {
    rejectValue: SignInError;
  }
>(
  'users/createUserWithEmailAndPassword',
  async (signInPayload: SignInPayload, thunkAPI) => {
    try {
      const userCredential: UserCredential = await createUserWithEmailAndPassword(auth, signInPayload.email, signInPayload.password);
      const user: AppUser = fromUserCredential(userCredential);
      return user;
    } catch (err: any) {
      if (err instanceof FirebaseError) {
        return thunkAPI.rejectWithValue({
          errorMessage: (err as FirebaseError).message,
          errorCode: (err as FirebaseError).code
        });
      }
      return thunkAPI.rejectWithValue({
        errorMessage: "Unexpected error while creating user with email and password.",
        errorCode: "error/unknown"
      });
    }
  }
);

export const callSignInWithEmailAndPassword = createAsyncThunk<
  AppUser,
  SignInPayload,
  {
    rejectValue: SignInError;
  }
>(
  'users/signInWithEmailAndPassword',
  async (signInPayload: SignInPayload, thunkAPI) => {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, signInPayload.email, signInPayload.password);
      const user: AppUser = fromUserCredential(userCredential);
      return user;
    } catch (err: any) {
      if (err instanceof FirebaseError) {
        return thunkAPI.rejectWithValue({
          errorMessage: (err as FirebaseError).message,
          errorCode: (err as FirebaseError).code
        });
      }
      return thunkAPI.rejectWithValue({
        errorMessage: "Unexpected error while signing in with email and password.",
        errorCode: "error/unknown"
      });
    }
  }
);

export const callSignout: AsyncThunk<
  void,
  void,
  any
> = createAsyncThunk(
  'users/signout',
  async () => await signOut(auth)
);

export const callSendPasswordResetEmail = createAsyncThunk<
  string, // return the email
  { email: string },
  {
    rejectValue: SignInError;
  }
>(
  'users/sendPasswordResetEmail',
  async ({ email }, thunkAPI) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return email;
    } catch (err) {
      if (err instanceof FirebaseError) {
        return thunkAPI.rejectWithValue({
          errorMessage: (err as FirebaseError).message,
          errorCode: (err as FirebaseError).code
        });
      }
      return thunkAPI.rejectWithValue({
        errorMessage: "Unexpected error while requesting password reset.",
        errorCode: "error/unknown"
      });
    }
  }
);

const googleAuthProvider = new GoogleAuthProvider();
export const callSignInWithGoogle = createAsyncThunk<
  AppUser,
  void,
  {
    rejectValue: SignInError;
  }
>(
  'users/signInWithGoogle',
  async (_: void, thunkAPI) => {
    try {
      const userCredential: UserCredential = await signInWithPopup(auth, googleAuthProvider);
      const user: AppUser = fromUserCredential(userCredential);
      return user;
    } catch (err: any) {
      if (err instanceof FirebaseError) {
        return thunkAPI.rejectWithValue({
          errorMessage: (err as FirebaseError).message,
          errorCode: (err as FirebaseError).code
        });
      }
      return thunkAPI.rejectWithValue({
        errorMessage: "Unexpected error while signing in.",
        errorCode: "error/unknown"
      });
    }
  }
);


export interface UserState {
  user: AppUser | null;
}

const initialState: UserState = {
  user: null
};
Object.assign(initialState, JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"));

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    listenerUpdateUser: (state: UserState, action: PayloadAction<AppUser>) => {
      state.user = action.payload;
    },
    login: (state: UserState, action: PayloadAction<AppUser>) => {
      state.user = action.payload;
    },
    logout: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<UserState>) => {
    builder
      .addCase(callSignInWithGoogle.fulfilled, (_: UserState, action: PayloadAction<AppUser>) => {
        notify(`Je bent ingelogd als ${action.payload.displayName || action.payload.email}`, SuccessNotificationParams);
      })
      .addCase(callSignInWithGoogle.rejected, (_: UserState, action: any) => {
        const error = action.payload as SignInError;
        console.error(`Error code: ${error.errorCode}, Error message: ${error.errorMessage}`);
        notify(`Inloggen met Google mislukt: ${error.errorMessage}`, ErrorNotificationParams);
      })
      .addCase(callCreateUserWithEmailAndPassword.fulfilled, (_: UserState, action: PayloadAction<AppUser>) => {
        notify(`Een nieuwe account ${action.payload.displayName || action.payload.email} is succesvol aangemaakt.`, SuccessNotificationParams);
      })
      .addCase(callCreateUserWithEmailAndPassword.rejected, (_: UserState, action: any) => {
        const error = action.payload as SignInError;
        console.error(`Error code: ${error.errorCode}, Error message: ${error.errorMessage}`);
        notify(`Account aanmaken mislukt: ${error.errorMessage}`, ErrorNotificationParams);
      })
      .addCase(callSignInWithEmailAndPassword.fulfilled, (_: UserState, action: PayloadAction<AppUser>) => {
        notify(`Je bent ingelogd als ${action.payload.displayName || action.payload.email}`, SuccessNotificationParams);
      })
      .addCase(callSignInWithEmailAndPassword.rejected, (_: UserState, action) => {
        const error = action.payload as SignInError;
        console.error(`Error code: ${error.errorCode}, Error message: ${error.errorMessage}`);
        notify(`Inloggen mislukt: ${error.errorMessage}`, ErrorNotificationParams);
      })
      .addCase(callSignout.fulfilled, (_state: UserState, _action: PayloadAction<void>) => {
        notify(`Je bent nu uitgelogd.`, InfoNotificationParams);
      })
      .addCase(callSendPasswordResetEmail.fulfilled, (_: UserState, action: PayloadAction<string>) => {
        notify(`Een paswoord reset email is verstuurd naar ${action.payload}`, NeutralNotificationParams);
      })
      .addCase(callSendPasswordResetEmail.rejected, (_: UserState, action) => {
        const error = action.payload as SignInError;
        console.error(`Error code: ${error.errorCode}, Error message: ${error.errorMessage}`);
        notify(`Paswoord kan niet reset worden: ${error.errorMessage}`, ErrorNotificationParams);
      })
  },
});

const usersListenerMiddleware = createListenerMiddleware();

// LOGIN LISTENER
usersListenerMiddleware.startListening({
  actionCreator: userSlice.actions.login,
  effect: async (action: PayloadAction<AppUser>, listenerApi) => {
    const user: AppUser = action.payload;
    await saveUser(user);
    // check if the user already has lists in firestore
    const listCount: number = await getListCount(user);
    if (listCount === 0) {
      // if not, we save the UNSAVED_ID list to firestore
      const list: ProductList = getSelectedList(listenerApi.getState() as RootState);
      await saveList(user, list);
    }
    await listenerApi.dispatch(startListeningUser(user));
    await listenerApi.dispatch(startListeningUserLists(user));
  },
});

// LOGOUT LISTENER
usersListenerMiddleware.startListening({
  actionCreator: userSlice.actions.logout,
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(stopListeningUser());
    await listenerApi.dispatch(stopListeningUserLists());
  },
});


export const usersMiddleWare = usersListenerMiddleware.middleware;

export const { login, logout, listenerUpdateUser } = userSlice.actions;

// selectors
export const selectUser = (state: RootState): (AppUser | null) => state?.user?.user || null;
export const userReducer = userSlice.reducer;